/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2023-02-22 10:46:47
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './index.scss'
import AMapLoader from '@amap/amap-jsapi-loader'
import CONFIG from '../../../../constants/config'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import actionTypes from '../../../../constants/actionTypes'
import {
  AreaOverView,
  convertMaxNumberStr,
  DistrictsStatistic,
  PhotoAddrStatisticResp,
  POIItemOverView
} from '../../index'
// 全局类型已在 src/types/amap.d.ts 中声明
// 每个区域对应的限制地图范围，如 district 对应 10~12
const AREA_ZOOM = {
  district: [10, 10.5, 11, 11.5, 12],
  poi: [
    12.5,
    13,
    13.5,
    14,
    14.5,
    15,
    15.5,
    16,
    16.5,
    17,
    17.5,
    18,
    18.5,
    19,
    19.5,
    20
  ]
}
// 地图范围对应要展示的标记点，all代表全部，number 表示具体的数量
const ZOOM_POINT_MAP: {
  [key: string]: number | 'all'
} = {
  10: 'all',
  12: 'all',
  14: 100,
  16: 'all',
  18: 'all',
  20: 'all'
}
interface IProps {
  mapData: PhotoAddrStatisticResp
  handleClickMarker(item: AreaOverView | POIItemOverView, extra?: any): void
  setCurrentOverView: any
  maxPoint?: number
}

// 地图初始状态时的数据
type MapStatusInfo = {
  lng: number | string
  lat: number | string
  zoom: number
}

const MapContainer: React.FC<IProps> = ({
  mapData,
  handleClickMarker,
  setCurrentOverView,
  maxPoint: maxMarkerPoint
}) => {
  const { cityOverView, districtsStatistic } = mapData
  const map = useRef<any>()
  const prevZoom = useRef(16)
  const groupState = useAppSelector((state) => state.group)
  // const { isVip } = groupState.toJS()
  const isVip = true // 303版本 该功能限免 默认都是Vip
  const currentDistrictStatistic = useRef<DistrictsStatistic | null>(null)
  const initComplete = useRef<Boolean>(false)
  const dispatch = useAppDispatch()
  const sourceMapStatusInfo = useRef<MapStatusInfo>({} as MapStatusInfo)
  const [curMapStatusInfo, setCurMapStatusInfo] = useState<MapStatusInfo>(
    {} as MapStatusInfo
  )

  // 根据地图上左上角、右下角的一个范围经纬度去筛选统计数据
  const getDistrictStatisticByBounds = useCallback(() => {
    const { northEast, southWest } = map.current.getBounds()
    const minLng = southWest.lng
    const minLat = southWest.lat
    const maxLng = northEast.lng
    const maxLat = northEast.lat
    let statisticArr: Array<POIItemOverView> = []
    districtsStatistic.forEach(({ poiStatistic }) => {
      poiStatistic.forEach((item) => {
        const { lng, lat } = item
        if (lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat) {
          statisticArr.push(item)
        }
      })
    })
    return statisticArr
  }, [districtsStatistic])

  // 获取地区marker content str
  const getDistrictsMarkerContent = useCallback((item: AreaOverView) => {
    const { name, photoAddrCnt, photoCnt, photoUserCnt } = item
    return `<div class='districts-marker'>
              <div class='districts-name'>${name}</div>
               <div class='districts-position'>
                  <span>拍照地点</span>
                  <span>${convertMaxNumberStr(photoAddrCnt, 99999)}</span>
               </div>
               <div class='districts-photo'>
                 <div><span>${convertMaxNumberStr(
                   photoCnt,
                   99999
                 )}</span>张照片</div>
                 <div><span>${convertMaxNumberStr(
                   photoUserCnt,
                   99999
                 )}</span>人拍照</div>
               </div>
           </div>`
  }, [])

  // 获取不同层级显示点数
  const getMaxPoint = useCallback((zoom: number): number | 'all' => {
    if (zoom >= 12.5 && zoom <= 13.5) {
      return 50
    }
    if (zoom > 13.5 && zoom <= 15) {
      return 100
    }
    if (zoom > 15 && zoom <= 16.5) {
      return 150
    }
    return 'all'
  }, [])

  // 标记地区点
  const markerDistrictPoint = useCallback(() => {
    if (!districtsStatistic.length) return
    // 每次重新绘制先清除之前标记点
    map.current.clearMap()
    let zoom = map.current!.getZoom()
    setCurrentOverView(cityOverView)
    currentDistrictStatistic.current = null
    /*const maxPoint = Math.min(
      districtsStatistic.length,
      maxMarkerPoint as number
    )*/
    const maxPointResult = getMaxPoint(zoom)
    const maxPoint: number =
      maxPointResult === 'all'
        ? districtsStatistic.length
        : Math.min(maxPointResult, districtsStatistic.length)
    for (let i = 0; i < maxPoint; i++) {
      const { districtOverView, poiStatistic } = districtsStatistic[i]
      const marker = new window.AMap.Marker({
        position: new window.AMap.LngLat(
          districtOverView.lng,
          districtOverView.lat
        ),
        offset: new window.AMap.Pixel(-60, -60),
        content: getDistrictsMarkerContent(districtOverView)
      })
      marker.on('click', (e) => {
        handleClickMarker(districtOverView)
        if (isVip) {
          const centerPoi =
            poiStatistic.length > 0
              ? [poiStatistic[0].lng, poiStatistic[0].lat]
              : [districtOverView.lng, districtOverView.lat]
          map.current.setZoomAndCenter(14, centerPoi)
          prevZoom.current = 14
          currentDistrictStatistic.current = { districtOverView, poiStatistic }
        }
      })
      // 将 markers 添加到地图
      map.current.add(marker)
    }
  }, [
    getMaxPoint,
    isVip,
    cityOverView,
    setCurrentOverView,
    handleClickMarker,
    districtsStatistic,
    getDistrictsMarkerContent,
    maxMarkerPoint
  ])

  // 获取POI marker content str
  const getPOIMarkerContent = useCallback((item: POIItemOverView) => {
    const getSmallUrl = (url) => {
      if (url.includes('?x-oss-process')) return url + ',w_42'
      return url
    }
    const { addrName, photoCnt, photoCover, latestPhotoDay, lat, lng } = item
    return `<div class='poi-marker' data-lnglat=${lng + '-' + lat} >
              <div class='img-wrapper'>
                <img src=${getSmallUrl(photoCover)} alt=''>
                <span>
                  ${convertMaxNumberStr(photoCnt, 9999)}张
                </span>
              </div>
              <div class='desc'>
                <span>${addrName}</span>
                <span>${latestPhotoDay}天前到访</span>
              </div>
            <div class='triangle'/>
        </div>`
  }, [])

  const addPOIMarker = useCallback(
    (poiStatistic) => {
      if (!poiStatistic.length) return
      let zoom = map.current!.getZoom()
      // const maxPoint = Math.min(poiStatistic.length, maxMarkerPoint as number)
      let maxPoint = getMaxPoint(zoom)
      maxPoint =
        maxPoint === 'all'
          ? poiStatistic.length
          : Math.min(maxPoint as number, poiStatistic.length)
      console.log('poi:', poiStatistic.length)
      console.log('maxPoi:', maxPoint)
      let poiStatistic_ = poiStatistic.slice()
      poiStatistic_ =
        poiStatistic_.length > maxPoint
          ? poiStatistic_.sort(() => Math.random() - 0.5)
          : poiStatistic_ // 随机显示
      for (let i = 0; i < maxPoint; i++) {
        const POIItem = poiStatistic_[i]
        if (!POIItem) continue
        const marker = new window.AMap.Marker({
          position: new window.AMap.LngLat(POIItem.lng, POIItem.lat),
          offset: new window.AMap.Pixel(-60, 0),
          content: getPOIMarkerContent(POIItem)
        })
        marker.on('click', (e) => {
          map.current.getCity((cityInfo) => {
            let markerEles = document.querySelectorAll('.poi-marker')
            Array.from(markerEles).forEach((ele) => {
              // @ts-ignore
              ele.parentNode.classList.remove('top-zIndex')
              ele.classList.remove('active')
              // @ts-ignore 设置气泡选中样式
              if (ele.dataset.lnglat === POIItem.lng + '-' + POIItem.lat) {
                // @ts-ignore
                ele.parentNode.classList.add('top-zIndex')
                ele.classList.add('active')
              }
            })
            handleClickMarker(POIItem, cityInfo)
          }, e.target.getPosition())
        })
        // 将 markers 添加到地图
        map.current.add(marker)
      }
    },
    [handleClickMarker, getPOIMarkerContent, maxMarkerPoint, getMaxPoint]
  )

  // 地图初始化首次绘制POI地点
  const initMarkerPOIPoint = useCallback(() => {
    // 每次重新绘制先清除之前标记点
    map.current.clearMap()
    // 有设置区域统计数据
    let poiStatistic
    if (currentDistrictStatistic.current) {
      poiStatistic = currentDistrictStatistic.current.poiStatistic
    }
    if (!poiStatistic.length) return
    // 以第一个点为中心展开
    map.current.setZoomAndCenter(16, [poiStatistic[0].lng, poiStatistic[0].lat])
    // 调用上面的方法会改变地图层级，所以要设置回默认的层级
    setTimeout(() => {
      addPOIMarker(poiStatistic)
      prevZoom.current = 16
      initComplete.current = true
    }, 2000)
  }, [addPOIMarker])

  // 标记POI点
  const markerPOIPoint = useCallback(() => {
    // 每次重新绘制先清除之前标记点
    map.current.clearMap()
    // 有设置区域统计数据
    let poiStatistic
    if (currentDistrictStatistic.current) {
      poiStatistic = currentDistrictStatistic.current.poiStatistic
    } else {
      // 根据地图显示范围设置区域统计数据
      poiStatistic = getDistrictStatisticByBounds()
    }
    addPOIMarker(poiStatistic)
  }, [addPOIMarker, getDistrictStatisticByBounds])

  // 监听地图范围变化开始时
  const handleZoomStart = useCallback(() => {
    // prevZoom.current = map.current!.getZoom()
    console.log('zoomstart:', prevZoom.current)
  }, [])

  // 监听地图范围变化结束时
  const handleZoomEnd = useCallback(() => {
    if (!initComplete.current) return
    setTimeout(() => {
      //  保存一份当前地图中心点和层级
      const center = map.current.getCenter()
      setCurMapStatusInfo({
        lat: center.lat,
        lng: center.lng,
        zoom: map.current.getZoom()
      })
    }, 1000)
    let zoom = map.current.getZoom()
    console.log('zoomend：', zoom)
    let isDistrictZoom = AREA_ZOOM.district.includes(zoom)
    let isPOIZoom = AREA_ZOOM.poi.includes(zoom)
    // 显示地区县点
    if (isDistrictZoom) {
      prevZoom.current = zoom
      markerDistrictPoint()
      return
    }
    // 显示具体poi点
    if (isPOIZoom) {
      prevZoom.current = zoom
      markerPOIPoint()
      return
    }
    let allZoom = new Set([...AREA_ZOOM.district, ...AREA_ZOOM.poi])
    //当上一次地图范围变化结束时，判断当前地图操作是放大/缩小，且只能放大/缩小到指定的地图范围
    const setAssignZoom = () => {
      let isEnlarge = zoom - prevZoom.current > 0
      console.log(isEnlarge ? '放大' : '缩小')
      let zooms = Array.from(allZoom).sort((a, b) => a - b) // 1,2,3,4,5 ...
      for (let i = 0; i < zooms.length - 1; i++) {
        if (zoom > zooms[i] && zoom < zooms[i + 1]) {
          let newZoom = isEnlarge ? zooms[i + 1] : zooms[i]
          map.current.setZoom(newZoom, true)
          prevZoom.current = newZoom
          return
        }
      }
    }
    setAssignZoom()
  }, [markerDistrictPoint, markerPOIPoint])

  // 地图拖拽平移结束时
  const handleMoveEnd = useCallback(() => {
    if (!initComplete.current) return
    currentDistrictStatistic.current = null
    handleZoomEnd()
  }, [handleZoomEnd])

  // 绑定地图事件
  const clickOnMap = useCallback(() => {
    if (!map.current) return
    map.current.on('zoomstart', handleZoomStart)
    map.current.on('zoomend', handleZoomEnd)
    map.current.on('moveend', handleMoveEnd)
  }, [handleZoomStart, handleZoomEnd, handleMoveEnd])

  // 解绑地图事件
  const clickOffMap = useCallback(() => {
    if (!map.current) return
    map.current.off('zoomstart', handleZoomStart)
    map.current.off('zoomend', handleZoomEnd)
    map.current.off('moveend', handleMoveEnd)
  }, [handleZoomStart, handleZoomEnd, handleMoveEnd])

  useEffect(() => {
    if (!cityOverView) return
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })
    AMapLoader.load({
      key: CONFIG.GAODE_KEY, //需要设置您申请的key
      version: '2.0',
      plugins: ['AMap.ToolBar'],
      AMapUI: {
        version: '1.1',
        plugins: []
      }
    })
      .then((AMap) => {
        // 获取第一个有poi点的区县
        const getDistrict = () => {
          for (let i = 0; i < districtsStatistic.length; i++) {
            const district = districtsStatistic[i]
            if (district && district.poiStatistic.length) {
              return district
            }
          }
          return false
        }
        const district = getDistrict()
        // @ts-ignore
        const overView = district ? district.districtOverView : cityOverView
        const options: any = {
          zoom: district ? 16 : 12,
          zooms: [10, 20],
          mapStyle: 'amap://styles/normal',
          jogEnable: false // 地图是否使用缓动效果
        }
        if (overView.lng && overView.lat) {
          options.center = [overView.lng, overView.lat]
        }
        window.MapInstance = map.current = new AMap.Map('mapContainer', options)
        if (isVip) {
          map.current.addControl(
            new AMap.ToolBar({
              position: 'RB',
              offset: [40, 40]
            })
          )
          /*window.AMapUI!.loadUI(['control/BasicControl'], function (
            BasicControl
          ) {
            //添加一个缩放控件
            // @ts-ignore
            map.current!.addControl(
              new BasicControl.Zoom({
                position: 'rb',
                showZoomNum: true
              })
            )
          })*/
        }
        if (district) {
          // 默认绘制第一个市区点下面的POI集合
          // @ts-ignore
          currentDistrictStatistic.current = district
          initMarkerPOIPoint()
        } else {
          markerDistrictPoint()
          // 尽量让标记点都显示在一屏内
          map.current.setFitView()
          // 调用上面的方法会改变地图层级，所以要设置回默认的层级
          setTimeout(() => {
            map.current.setZoom(12)
            prevZoom.current = 12
            initComplete.current = true
          }, 500)
        }
        setTimeout(() => {
          if (!isVip) {
            map.current.setStatus({
              keyboardEnable: false, // 地图是否可通过键盘控制
              doubleClickZoom: false, // 地图是否可通过双击鼠标放大地图
              zoomEnable: false // 地图是否可缩放
            })
          }
          dispatch({
            type: actionTypes.SET_LOADING,
            data: false
          })
          // 保存一份当前地图中心点和层级
          const center = map.current.getCenter()
          sourceMapStatusInfo.current = {
            lat: center.lat,
            lng: center.lng,
            zoom: map.current.getZoom()
          }
          setCurMapStatusInfo({
            lat: center.lat,
            lng: center.lng,
            zoom: map.current.getZoom()
          })
          clickOnMap()
        }, 2000)
      })
      .catch((e) => {
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })
        console.log(e)
      })
    return () => {
      clickOffMap()
    }
  }, [
    isVip,
    dispatch,
    markerDistrictPoint,
    markerPOIPoint,
    districtsStatistic,
    clickOnMap,
    clickOffMap,
    cityOverView,
    maxMarkerPoint,
    initMarkerPOIPoint
  ])

  // 是否展示恢复默认, 当前中心点和层级与初始状态不一致时才出现按钮
  const showRestoreDefault = useMemo(() => {
    const { lng: curLng, lat: curLat, zoom: curZoom } = curMapStatusInfo
    const { lng, lat, zoom } = sourceMapStatusInfo.current
    const isShow = curLng !== lng || curLat !== lat || curZoom !== zoom
    return cityOverView && isVip && isShow
  }, [cityOverView, isVip, curMapStatusInfo])

  return (
    <div className="map-out-container">
      <div id="mapContainer" />
      {showRestoreDefault && (
        <div
          className="restore-default"
          onClick={() => {
            let { zoom, lat, lng } = sourceMapStatusInfo.current
            setCurrentOverView(cityOverView)
            prevZoom.current = zoom
            map.current.setZoomAndCenter(zoom, [lng, lat])
          }}
        >
          恢复默认
        </div>
      )}
    </div>
  )
}
export default MapContainer
